import { Box, CircularProgress, Grid } from "@mui/material";
import {
  InnerWrapper,
  MainContainer,
  RangePicker,
  TitleAndActionBox,
} from "../../../common";
import {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LanguageContext } from "../../../../context/LanguageContext";
import { ParagraphText, UBButton } from "../../../../Libraries";
import {
  CalendarIconNew,
  DownloadIconBlack,
} from "../../../../assets/icons/icon";
import RendorList from "./RendorList";
import { useNavigate } from "react-router-dom";
import styles from "./styles.module.css";
import { DEFAULT_DATE_FILTERS_EXPORT, DEFAULT_DATE_FILTERS_REPORTS } from "../../../../constants";
import { useBusinessOverview } from "../../../../hooks/useBusinessOverview";
import { useAuthContext } from "../../../../context/AuthContext";
import moment from "moment";
import { formatRange, mapCategoryToList, getStatementDateConfig } from "../../../../utils";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import useDownlaodPdf from "../../../../hooks/useDownlaodPdf";
import { PDF_DOWNLOAD_NEW } from "../../../../apis/configuration";

const { minDate, maxDate } = getStatementDateConfig();
const defaultStart = moment().subtract(29, "days").startOf("day").toDate();
const defaultEnd = moment().endOf("day").toDate();

const getStoredFilter = () => {
  try {
    const saved = JSON.parse(sessionStorage.getItem("bo_pnl_filter"));
    if (!saved?.start_date || !saved?.end_date) return null;
    return { start_date: new Date(saved.start_date), end_date: new Date(saved.end_date) };
  } catch { return null; }
};

const ProfitAndLoss = () => {
  const [I18n] = useContext(LanguageContext);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [dateFilterOpen, setIsDateFilterOpen] = useState(false);
  const [loading, setIsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => {
    const saved = getStoredFilter();
    return { start_date: saved?.start_date || defaultStart, end_date: saved?.end_date || defaultEnd };
  });
  const navigate = useNavigate();
  const { business, user } = useAuthContext();
  const isApiCalled = useRef(true);

  const { onDownload, downloadLoading } = useDownlaodPdf(
    user,
    PDF_DOWNLOAD_NEW,
  );

  const {
    handleGetPNLStatement,
    net_profit,
    expenses_category,
    sales_category,
    total_business_sales,
    gross_profit,
    expenses,
    cost_of_sales,
    pnl_statement_loading,
  } = useBusinessOverview({ business_id: business?.id, navigate });

  useEffect(() => {
    const saved = getStoredFilter();
    handleGetPNLStatement({ startDate: saved?.start_date || defaultStart, endDate: saved?.end_date || defaultEnd });
    isApiCalled.current = false;
  }, []);

  // Category names are resolved in the selected language when the statement
  // is built, so rebuild it for the same date range when the language changes
  const lastI18nRef = useRef(I18n);
  useEffect(() => {
    if (lastI18nRef.current === I18n) return;
    lastI18nRef.current = I18n;
    const { start_date, end_date } = selectedDate;
    handleGetPNLStatement(
      start_date && end_date ? { startDate: start_date, endDate: end_date } : undefined,
    );
  }, [I18n]);

  const handleApplyFilter = (startDate, endDate) => {
    setSelectedDate({ start_date: startDate, end_date: endDate });
    setIsDateFilterOpen(false);
    if (!startDate || !endDate) {
      handleGetPNLStatement();
    } else {
      sessionStorage.setItem("bo_pnl_filter", JSON.stringify({ start_date: startDate, end_date: endDate }));
      handleGetPNLStatement({ startDate, endDate });
    }
  };

  // const handleDownloadStatement = (startDate, endDate) => {
  //   triggerLogEvent(analytics, "pnl_report_download");
  //   setIsExportOpen(false);
  //   let paramsForReport = {
  //     template_name: "accountreport_new.hbs",
  //     business_id: business.id,
  //     phone_number: user?.phone_number,
  //     type: "profit-loss",
  //     pdf: true,
  //     start_date: startDate ? moment(startDate).format("DD MMM YYYY") : "",
  //     end_date: endDate ? moment(endDate).format("DD MMM YYYY") : "",
  //   };

  //   onDownload(paramsForReport, () => {}, navigate);
  // };

  const summary = useMemo(() => {
    return [
      { label: "sales", value: total_business_sales },
      { label: "cost_of_sales", value: cost_of_sales },
      { label: "gross_profit", value: gross_profit },
      { label: "expenses", value: expenses },
      { label:net_profit > 0 ? "net_profit" : "net_loss", value: net_profit, isSign: true },
    ];
  }, [cost_of_sales, gross_profit, expenses, net_profit]);

  const salesList = useMemo(
    () => mapCategoryToList(sales_category || {}),
    [sales_category],
  );

  const expenseList = useMemo(
    () => mapCategoryToList(expenses_category || {}),
    [expenses_category],
  );

  return (
    <MainContainer fluid className="quickSalePage">
      <Grid container>
        <Grid item md={12}>
          <TitleAndActionBox data={{ isBack: true, title: I18n.profit_Loss }} />

          <InnerWrapper className={`innerWrapper ${styles.mainWrapper}`}>
            {pnl_statement_loading || isApiCalled?.current ? (
              <Box className="d-flex justify-content-center align-items-center h-100">
                <CircularProgress size="30px" />
              </Box>
            ) : (
              <Fragment>
                <Box
                  className={`d-flex align-items-center justify-content-between ${styles.header}`}
                >
                  <ParagraphText
                    className={"fontSize18 fontWeight500 newFontColor"}
                  >
                    {I18n.profit_Loss_statement}
                  </ParagraphText>

                  <Box display={"flex"} gap={"12px"}>
                    <Box className={styles.dateContainer}>
                      <UBButton
                        id="profit_loss_filter_btn"
                        title={formatRange(selectedDate, I18n)}
                        className={styles.exportButton}
                        onClick={() => setIsDateFilterOpen(!dateFilterOpen)}
                        spinnerColor={"black"}
                        iconComponent={
                          loading ? null : (
                            <CalendarIconNew width="12px" height="12px" />
                          )
                        }
                        loading={loading}
                      />
                      {dateFilterOpen ? (
                        <RangePicker
                          onFilterDate={(startDate, endDate) => {
                            handleApplyFilter(startDate, endDate);
                          }}
                          isOpen={dateFilterOpen}
                          setIsOpen={setIsExportOpen}
                          showDirectPicker={true}
                          rangeValue={selectedDate?.start_date && selectedDate?.end_date ? [selectedDate.start_date, selectedDate.end_date] : []}
                          noClearBtn={true}
                          dateFilters={DEFAULT_DATE_FILTERS_REPORTS}
                          minDate={minDate}
                          maxDate={maxDate}
                        />
                      ) : null}
                    </Box>
                    {/* <Box className={styles.dateContainer}>
                      <UBButton
                        title={I18n.export}
                        className={styles.exportButton}
                        onClick={() => setIsExportOpen(!isExportOpen)}
                        spinnerColor={"black"}
                        iconComponent={
                          loading ? null : (
                            <DownloadIconBlack width="12px" height="12px" />
                          )
                        }
                        loading={loading}
                      />
                      {isExportOpen ? (
                        <RangePicker
                          onFilterDate={handleDownloadStatement}
                          isOpen={isExportOpen}
                          setIsOpen={setIsExportOpen}
                          showDirectPicker={true}
                          rangeValue={[]}
                          noClearBtn={true}
                          dateFilters={DEFAULT_DATE_FILTERS_EXPORT}
                        />
                      ) : null}
                    </Box> */}
                  </Box>
                </Box>

                <RendorList title={"summary"} list={summary} />

                {salesList?.length ? (
                  <RendorList
                    title={"sales"}
                    list={salesList}
                    seeMoreLess={salesList.length > 3}
                    onClick={(category) =>
                      navigate("/CategorizedList", {
                        state: { category, type: "Sale" },
                      })
                    }
                  />
                ) : null}
                {expenseList?.length ? (
                  <RendorList
                    title={"expenses"}
                    list={expenseList}
                    seeMoreLess={expenseList.length > 3}
                    onClick={(category) =>
                      navigate("/CategorizedList", {
                        state: { category, type: "Expense" },
                      })
                    }
                  />
                ) : null}
              </Fragment>
            )}
          </InnerWrapper>
        </Grid>
      </Grid>
    </MainContainer>
  );
};
export default ProfitAndLoss;
